import React, { useEffect, useState } from "react";
import { api, formatBRL, formatDateTimeBR } from "../lib/api";
import { Layers, Loader2, Check, FileText, Handshake } from "lucide-react";
import { toast } from "sonner";

export default function InstallmentCommissionsSection({ isAdmin }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [selected, setSelected] = useState({});
  const [openInd, setOpenInd] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/installment-commissions/report");
      setReport(data);
    } catch (_) {
      setReport({ indicators: [] });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleSel = (iid, cid) => {
    setSelected((prev) => ({
      ...prev,
      [iid]: { ...(prev[iid] || {}), [cid]: !(prev[iid]?.[cid]) },
    }));
  };

  const pay = async (g) => {
    if (!isAdmin) return;
    const ids = Object.keys(selected[g.indicator_id] || {}).filter((id) => selected[g.indicator_id][id]);
    const pendings = g.items.filter((c) => c.status === "pendente");
    const idsToPay = ids.length ? ids : pendings.map((c) => c.installment_contract_id);
    if (!idsToPay.length) return toast.error("Nenhuma comissão pendente");
    if (!window.confirm(`Pagar ${idsToPay.length} comissão(ões) de parcelado/acordo para ${g.indicator_name}?`)) return;
    setPaying(g.indicator_id);
    try {
      await api.post("/installment-commissions/pay", {
        indicator_id: g.indicator_id,
        installment_contract_ids: idsToPay,
      });
      toast.success("Pagamento registrado");
      setSelected((prev) => ({ ...prev, [g.indicator_id]: {} }));
      load();
    } catch (e) {
      toast.error("Falha ao pagar");
    } finally {
      setPaying(null);
    }
  };

  if (loading) {
    return (
      <div className="fc-card p-5 mt-6" data-testid="installment-commissions-loading">
        <div className="flex items-center gap-2 text-[#737373] text-sm">
          <Loader2 size={14} className="animate-spin" /> Carregando comissões de parcelados...
        </div>
      </div>
    );
  }

  const groups = report?.indicators || [];

  return (
    <div className="mt-8" data-testid="installment-commissions-section">
      <div className="flex items-center gap-2 mb-4">
        <Layers size={16} className="text-[#D4AF37]" />
        <h2 className="font-display text-xl font-semibold">Comissões — Parcelados e Acordos</h2>
        <span className="text-[11px] text-[#737373] ml-2">(módulo separado dos contratos normais)</span>
      </div>

      {groups.length === 0 ? (
        <div className="fc-card p-8 text-center text-[12.5px] text-[#737373]">
          Nenhuma comissão de parcelado ou acordo registrada.
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => {
            const pendings = g.items.filter((c) => c.status === "pendente");
            const isOpen = openInd[g.indicator_id] !== false; // default aberto
            return (
              <div key={g.indicator_id || "none"} className="fc-card overflow-hidden" data-testid={`ic-commission-group-${g.indicator_id || "none"}`}>
                <div className="p-4 flex items-center justify-between gap-3 cursor-pointer hover:bg-[#0d0d0d]" onClick={() => setOpenInd((p) => ({ ...p, [g.indicator_id]: !isOpen }))}>
                  <div className="min-w-0">
                    <div className="text-[14px] font-semibold text-white">{g.indicator_name}</div>
                    <div className="text-[11px] text-[#737373] mt-0.5">
                      {g.items.length} comissões · Pendente: <span className="text-amber-300">{formatBRL(g.total_pendente)}</span>
                      {" · "}Paga: <span className="text-emerald-300">{formatBRL(g.total_paga)}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-[#737373]">Total</div>
                    <div className="font-mono text-[#D4AF37] text-[15px]">{formatBRL(g.total_geral)}</div>
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-[#1f1f1f] p-4 bg-[#0a0a0a]">
                    <div className="overflow-x-auto">
                      <table className="w-full text-[12.5px]">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                            {isAdmin && <th className="text-left py-1.5 w-6"></th>}
                            <th className="text-left py-1.5">Tipo</th>
                            <th className="text-left py-1.5">Cliente</th>
                            <th className="text-right py-1.5">Base</th>
                            <th className="text-right py-1.5">%</th>
                            <th className="text-right py-1.5">Valor</th>
                            <th className="text-left py-1.5 pl-3">Status</th>
                            <th className="text-left py-1.5">Pago em</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.items.map((c) => {
                            const Icon = c.kind === "parcelado" ? FileText : Handshake;
                            const kindCls = c.kind === "parcelado" ? "text-blue-300" : "text-violet-300";
                            const sel = selected[g.indicator_id]?.[c.installment_contract_id];
                            const isPending = c.status === "pendente";
                            return (
                              <tr key={c.id} className="border-b border-[#141414]">
                                {isAdmin && (
                                  <td className="py-1.5">
                                    {isPending && (
                                      <input
                                        type="checkbox"
                                        checked={!!sel}
                                        onChange={() => toggleSel(g.indicator_id, c.installment_contract_id)}
                                        data-testid={`select-ic-comm-${c.id}`}
                                      />
                                    )}
                                  </td>
                                )}
                                <td className="py-1.5">
                                  <span className={`inline-flex items-center gap-1 ${kindCls}`}>
                                    <Icon size={11} /> {c.kind}
                                  </span>
                                </td>
                                <td className="py-1.5 text-white">
                                  <a href={`/parcelados/${c.installment_contract_id}`} className="hover:text-[#D4AF37]">
                                    Ver contrato
                                  </a>
                                </td>
                                <td className="py-1.5 text-right font-mono text-[#A3A3A3]">{formatBRL(c.base_amount)}</td>
                                <td className="py-1.5 text-right text-[#A3A3A3]">{c.percent}%</td>
                                <td className="py-1.5 text-right font-mono text-white">{formatBRL(c.amount)}</td>
                                <td className="py-1.5 pl-3">
                                  <span className={`badge-base text-[10px] border ${
                                    c.status === "paga" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" :
                                    c.status === "cancelada" ? "bg-zinc-500/15 text-zinc-300 border-zinc-500/40" :
                                    "bg-amber-500/15 text-amber-300 border-amber-500/40"
                                  }`}>{c.status}</span>
                                </td>
                                <td className="py-1.5 text-[11px] text-[#737373]">{c.paid_at ? formatDateTimeBR(c.paid_at) : "—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {isAdmin && pendings.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-[#1f1f1f] flex items-center justify-end gap-2">
                        <button
                          onClick={() => pay(g)}
                          disabled={paying === g.indicator_id}
                          className="fc-btn-gold flex items-center gap-2 text-[12px]"
                          data-testid={`pay-ic-comm-${g.indicator_id}`}
                        >
                          {paying === g.indicator_id ? <><Loader2 size={12} className="animate-spin"/> Pagando…</> : <><Check size={12}/> Pagar selecionadas / pendentes</>}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
